import React, { useEffect, useState } from 'react';
import { Button, Typography } from 'antd';
import { LockOutlined } from '@ant-design/icons';
import {tokenStorage} from "../utils/tokenStorage";

const { Text } = Typography;

const LOGIN_URL = '/';

/**
 * Checks whether a valid auth token exists in localStorage.
 * Adjust the key name to match your auth implementation.
 */
const hasValidToken = () => {
    return !tokenStorage.isTokenExpired() && !!tokenStorage.getIdToken();
};

const SessionExpiredScreen = () => {
    const [countdown, setCountdown] = useState(10);

    // Auto-redirect countdown
    useEffect(() => {
        if (countdown === 0) {
            window.location.href = LOGIN_URL;
            return;
        }
        const t = setTimeout(() => setCountdown(c => c - 1), 1000);
        return () => clearTimeout(t);
    }, [countdown]);

    return (
        <div style={styles.overlay}>
            {/* Subtle animated grid background */}
            <div style={styles.gridBg} />

            {/* Radial glow */}
            <div style={styles.glow} />

            <div style={styles.card}>
                {/* Icon */}
                <div style={styles.iconWrap}>
                    <LockOutlined style={styles.icon} />
                    <div style={styles.iconRing} />
                </div>

                {/* Headline */}
                <h1 style={styles.heading}>Session Expired</h1>

                {/* Body */}
                <Text style={styles.body}>
                    Your login session has expired. Please sign in again to continue accessing AudiDox.
                </Text>

                {/* Redirect button */}
                <Button
                    type="primary"
                    size="large"
                    onClick={() => { window.location.href = LOGIN_URL; }}
                    style={styles.btn}
                >
                    Go to Login Page
                </Button>

                {/* Countdown hint */}
                <Text style={styles.countdown}>
                    Redirecting automatically in{' '}
                    <span style={styles.countdownNum}>{countdown}s</span>
                </Text>
            </div>

            <style>{css}</style>
        </div>
    );
};

/**
 * Wrapper — renders children normally when authenticated,
 * otherwise renders the full-screen expired screen.
 */
const SessionGuard = ({ children }) => {
    if (!hasValidToken()) {
        return <SessionExpiredScreen />;
    }
    return children;
};

export { SessionExpiredScreen, SessionGuard };
export default SessionGuard;

/* ─── Styles ─────────────────────────────────────────────── */

const styles = {
    overlay: {
        position: 'fixed',
        inset: 0,
        background: '#0D0F14',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 9999,
        overflow: 'hidden',
        fontFamily: "'DM Mono', monospace",
    },
    gridBg: {
        position: 'absolute',
        inset: 0,
        backgroundImage: `
            linear-gradient(rgba(121,167,165,0.04) 1px, transparent 1px),
            linear-gradient(90deg, rgba(121,167,165,0.04) 1px, transparent 1px)
        `,
        backgroundSize: '40px 40px',
        maskImage: 'radial-gradient(ellipse 80% 80% at 50% 50%, black 40%, transparent 100%)',
    },
    glow: {
        position: 'absolute',
        width: '500px',
        height: '500px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(121,167,165,0.08) 0%, transparent 70%)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        pointerEvents: 'none',
    },
    card: {
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: '20px',
        background: 'rgba(22, 28, 28, 0.85)',
        border: '1px solid rgba(121,167,165,0.18)',
        borderRadius: '16px',
        padding: '56px 48px',
        maxWidth: '440px',
        width: '90%',
        backdropFilter: 'blur(12px)',
        boxShadow: '0 0 60px rgba(0,0,0,0.6), 0 0 0 1px rgba(121,167,165,0.08)',
        animation: 'fadeSlideUp 0.5s ease both',
        textAlign: 'center',
    },
    iconWrap: {
        position: 'relative',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginBottom: '4px',
    },
    icon: {
        fontSize: '36px',
        color: '#79A7A5',
        position: 'relative',
        zIndex: 1,
        animation: 'pulse 3s ease-in-out infinite',
    },
    iconRing: {
        position: 'absolute',
        width: '72px',
        height: '72px',
        borderRadius: '50%',
        border: '1px solid rgba(121,167,165,0.25)',
        animation: 'ringPulse 3s ease-in-out infinite',
    },
    heading: {
        margin: 0,
        fontSize: '26px',
        fontWeight: 600,
        color: '#F0F8F7',
        letterSpacing: '-0.5px',
        fontFamily: "'DM Mono', monospace",
    },
    body: {
        fontSize: '14px',
        color: '#6B8585',
        lineHeight: 1.7,
        maxWidth: '320px',
    },
    btn: {
        marginTop: '8px',
        height: '46px',
        paddingInline: '36px',
        borderRadius: '8px',
        fontSize: '14px',
        fontFamily: "'DM Mono', monospace",
        letterSpacing: '0.3px',
        background: '#79A7A5',
        borderColor: '#79A7A5',
        color: '#0D0F14',
        fontWeight: 600,
        transition: 'all 0.2s ease',
    },
    countdown: {
        fontSize: '12px',
        color: '#3D5454',
    },
    countdownNum: {
        color: '#79A7A5',
        fontWeight: 600,
    },
};

const css = `
    @import url('https://fonts.googleapis.com/css2?family=DM+Mono:wght@400;500;600&display=swap');

    @keyframes fadeSlideUp {
        from { opacity: 0; transform: translateY(20px); }
        to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pulse {
        0%, 100% { opacity: 1; }
        50%       { opacity: 0.55; }
    }
    @keyframes ringPulse {
        0%, 100% { transform: scale(1);    opacity: 0.4; }
        50%       { transform: scale(1.25); opacity: 0; }
    }
`;